import Spinner from "presentation/components/Shared/Spinner";
import { Column } from "primereact/column";
import {
  DataTable,
  DataTableFilterEvent,
  DataTableFilterMetaData,
  DataTablePageEvent,
  DataTableSortEvent,
} from "primereact/datatable";
import { Toast } from "primereact/toast";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import HeaderEmployeeGrid from "./HeaderTimesheetGrid";
import { TimesheetInputFilter } from "domain/models/timesheet-input-filter";
import { Timesheet } from "domain/entities/timesheet";
import { useTimesheetsByFilter } from "presentation/hooks/use-query-timesheet";
import StatusTimesheetDropdown from "./StatusTimesheetDropdown";
import { useFilterTimesheetStore } from "presentation/stores/use-filter-timesheet-store";
import { useAuthStore } from "presentation/stores/use-auth-store";
import { useTimesheetReportsByEmployee } from "presentation/hooks/use-query-timesheet-report";

import React from "react";
import { pdf } from "@react-pdf/renderer";
import TimesheetReportPDF from "../TimesheetReportPDF";
import { translateText } from "infra/translation/translate";
import { httpClient } from "infra/axios/axios-api";
import { useEmployeesByFilter } from "presentation/hooks/use-query-employees";
import PdfViewerDialog from "presentation/components/Shared/PdfViewerDialog";
import { useTripsheetsByFilter } from "presentation/hooks/use-query-tripsheet";
import { TripsheetInputFilter } from "domain/models/tripsheet-input-filter";

interface TimesheetGridProps {
  externalFilters?: {
    employeeId?: string;
    month: number | undefined;
    year: number | undefined;
    sortBy: string;
    sortOrder: string;
  };
  isDisable?: boolean;
}

const TimesheetGrid: React.FC<TimesheetGridProps> = ({
  externalFilters,
  isDisable,
}) => {
  const navigate = useNavigate();
  const { filters, setFilters } = useFilterTimesheetStore();
  const toast = useRef<Toast>(null);
  const [s3PathToDisplay, setS3PathToDisplay] = useState<string | null>(null);
  const { userEmployee } = useAuthStore();
  const [lazyParams, setLazyParams] = useState({
    sortBy: "year",
    sortOrder: "desc",
    limit: 50,
    page: 1,
    filters: {},
  });

  const {
    data: timesheets,
    isLoading,
    isError,
    error,
    refetch,
  } = useTimesheetsByFilter({
    sortBy: lazyParams.sortBy,
    sortOrder: lazyParams.sortOrder,
    page: lazyParams.page,
    limit: lazyParams.limit,
    employeeId: userEmployee?.id,
    ...lazyParams.filters,
  } as TimesheetInputFilter);

  const {
    data: tripsheets,
    isLoading: isLoadingTripsheets,
    isError: isErrorLoadingTripsheets,
    error: errorLoadingTripsheets,
    refetch: refetchTripsheets,
  } = useTripsheetsByFilter({
    employeeId: userEmployee?.id,
    status: "Assinado 1"
  } as TripsheetInputFilter);

  const [timesheetIdToReport, setTimesheetIdToReport] = useState("");
  

  const {
    data: timesheetReportData,
    isLoading: isLoadingReportData,
    isError: isErrorReportData,
    error: errorReportData,
    refetch: refetchReportData,
    isRefetching: isRefetchingReportData,
  } = useTimesheetReportsByEmployee(timesheetIdToReport, {
    sortBy: "day",
    sortOrder: "desc",
    page: 1,
    limit: 100,
  });

  const { data: employeeData } = useEmployeesByFilter({
    email: userEmployee?.emailAddress ?? "",
  });

  useEffect(() => {
    const filterObject = Object.fromEntries(
      Object.entries(filters)
        .map(([key, value]) => {
          if ("constraints" in value) {
            return [key, value.constraints?.[0]?.value ?? ""];
          }
          return [key, (value as DataTableFilterMetaData)?.value ?? ""];
        })
        .filter(
          ([_, value]) => value !== null && value !== undefined && value !== ""
        )
    );

    if (filterObject["employee.name"]) {
      filterObject.employeeName = filterObject["employee.name"];
    }

    const mergedFilters = {
      ...filterObject,
      ...(externalFilters || {}),
    };
    if (mergedFilters.employeeId) {
      mergedFilters["employeeId"] = undefined;
    }

    setLazyParams((prev) => ({
      ...prev,
      filters: mergedFilters,
      sortBy: externalFilters?.sortBy || prev.sortBy,
      sortOrder: externalFilters?.sortOrder || prev.sortOrder,
    }));
  }, [filters, externalFilters]);

  useEffect(() => {
    if (externalFilters) {
      setLazyParams((prev) => ({
        ...prev,
        filters: externalFilters,
      }));
    }
  }, [externalFilters]);

  useEffect(() => {
    refetch();
    refetchTripsheets();
    // setTimesheetDataSort([]);
  }, [lazyParams, refetch]);

  useEffect(() => {
    if (timesheetIdToReport) {
      refetchReportData();
    }
  }, [timesheetIdToReport]);

  // Este useEffect lida com o erro da busca do relatório de forma controlada.
  useEffect(() => {
    // Só executa se houver um erro E se estávamos de facto a tentar buscar um relatório.
    if (isErrorReportData && timesheetIdToReport && toast.current) {
      toast.current.show({
        severity: "warn",
        summary: "Aviso",
        detail:
          errorReportData.message ||
          "Não foi possível gerar o relatório para o registo selecionado.",
        life: 3000,
      });
      setTimesheetIdToReport(""); // Limpa o estado para parar de tentar buscar.
    }
  }, [isErrorReportData, errorReportData, timesheetIdToReport]);

  useEffect(() => {
    const generatePdf = async () => {
      if (!timesheetReportData?.data) {
        console.error("Dados do relatório não disponíveis");
        return;
      }

      if (timesheetReportData?.data.email === "no@email.pt" && toast.current) {
        toast.current.show({
          severity: "warn",
          summary: "Aviso",
          detail: "Sem dados no mês para gerar relatório.",
          life: 3000,
        });
        setTimesheetIdToReport("");
        return;
      }

      try {
        const blob = await pdf(
          <TimesheetReportPDF
            data={timesheetReportData.data}
            isSuportService={employeeData?.data?.[0]?.isSuportService ?? false}
          />
        ).toBlob();

        const formData = new FormData();
        const currentyDay =
          String(timesheetReportData?.data.month).padStart(2, "0") +
          "-" +
          timesheetReportData?.data.year;
        formData.append("file", blob, currentyDay + "-" + "timesheet" + ".pdf");

        const folder = `timesheets/${currentyDay}/${userEmployee?.id}`;
        const uploadResponse = await httpClient.post(
          `/assets/${folder}`,
          formData
        );

        const s3Path: string = uploadResponse.data.filePath;

        // Agora, passamos apenas o s3Path para o PdfViewerDialog
        setS3PathToDisplay(s3Path);
      } catch (error) {
        // Se a requisição falhar (e.g., 404, 500), o erro será capturado aqui.
        toast.current?.show({
          severity: "error",
          summary: "Erro ao Gerar Relatório",
          detail:
            "Não foi possível obter o ficheiro do servidor. Verifique se o registo existe.",
        });
        console.error("Erro ao gerar/obter PDF:", error);
      } finally {
        setTimesheetIdToReport("");
      }
    };

    if (timesheetReportData?.data && !isRefetchingReportData) {
      generatePdf();
    }
  }, [timesheetReportData, isRefetchingReportData, employeeData]);

  if (isLoading) return <Spinner />;

  if (isError && toast.current) {
    toast.current.show({
      severity: "error",
      summary: "Erro",
      detail: error?.message || "Ocorreu um erro inesperado",
      life: 1500,
    });
  }

  const handleGenerateReport = async (id?: string) => {
    if (id) {
      setTimesheetIdToReport(id);
    }
  };

  const showActionOptions = (rowData: Timesheet) => {
    const editStatus = ["Novo", "Editado"];

    return (
      <div className="spacing-icons">
        {editStatus.includes(rowData.status) && ((tripsheets?.data?.length ?? 0) > 0) && !isDisable && (
          <i
            className="pi pi-info-circle cursor-pointer"
            style={{ color: "#FE9900" }}
            title={`Existe(m) mapa(s) de quilómetros pendente(s) de assinatura. Apenas após os assinar, poderá editar e submeter o seu mapa de horas.`}
          />
        )}{" "}
        {editStatus.includes(rowData.status) && ((tripsheets?.data?.length ?? 0) === 0) && !isDisable && (
          <i
            className="pi pi-pencil cursor-pointer"
            style={{ color: "var(--primary-color)" }}
            onClick={() => navigate(`/timesheets/edit/${rowData.id}`)}
          />
        )}{" "}
        {(!editStatus.includes(rowData.status) || isDisable) && (
          <i
            className="pi pi-eye cursor-pointer"
            onClick={() =>
              navigate(`/timesheets/view/${rowData.id}/${rowData.employee.id}`)
            }
          />
        )}{" "}
        {(!editStatus.includes(rowData.status) || isDisable) && (
          <div className="spacing-icons">
            <i
              className="pi pi-paperclip cursor-pointer"
              onClick={() => navigate(`/timesheets/attachment/${rowData.id}`)}
            />{" "}
            <i
              className="pi pi-file-pdf cursor-pointer"
              onClick={() => handleGenerateReport(rowData.id)}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <DataTable
        value={timesheets?.data || []}
        lazy
        paginator
        first={(lazyParams.page - 1) * lazyParams.limit}
        rows={lazyParams.limit}
        totalRecords={timesheets?.pagination?.count || 0}
        loading={isLoading}
        onPage={(e: DataTablePageEvent) =>
          setLazyParams({
            ...lazyParams,
            page: e.page ? e.page + 1 : 1,
            limit: e.rows,
          })
        }
        sortField={lazyParams.sortBy}
        sortOrder={lazyParams.sortOrder === "asc" ? 1 : -1}
        onSort={(e: DataTableSortEvent) =>
          setLazyParams({
            ...lazyParams,
            sortBy: e.sortField,
            sortOrder:
              e.sortOrder === -1 ? "desc" : e.sortOrder === 1 ? "asc" : "",
          })
        }
        onFilter={(e: DataTableFilterEvent) => setFilters(e.filters)}
        filters={filters}
        size="small"
        resizableColumns
        columnResizeMode="expand"
        header={<HeaderEmployeeGrid />}
        rowsPerPageOptions={[5, 10, 25, 50]}
        paginatorLeft
        dataKey="id"
        globalFilterFields={["year", "month", "status"]}
      >
        <Column
          field="employee.name"
          header={translateText("Name", "pt")}
          sortable
          filter
          showFilterMatchModes={false}
        />
        <Column
          field="year"
          header={translateText("Year", "pt")}
          sortable
          filter
          showFilterMatchModes={false}
        />
        <Column
          field="month"
          header={translateText("Month", "pt")}
          sortable
          filter
          showFilterMatchModes={false}
        />
        <Column
          field="version"
          header={translateText("Version", "pt")}
          sortable
          filter
          showFilterMatchModes={false}
        />
        <Column
          header={translateText("Status", "pt")}
          sortable
          filter
          filterField="status"
          field="status"
          body={(rowData) => rowData.status}
          showFilterMatchModes={false}
          filterElement={(options) => (
            <StatusTimesheetDropdown options={options} />
          )}
        />

        <Column
          body={showActionOptions}
          header={translateText("Actions", "pt")}
          align="center"
          alignHeader="center"
        />
      </DataTable>
      <Toast ref={toast} position="bottom-right" />
      {s3PathToDisplay && (
        <PdfViewerDialog
          s3Path={s3PathToDisplay}
          onHide={() => setS3PathToDisplay(null)}
        />
      )}
    </div>
  );
};

export default TimesheetGrid;
